import type { PolicyDocument, PolicySection } from './types'
import { OPERATOR } from './operator'
import {
  CHILDREN,
  CRASHLYTICS_OVERSEAS_ROW,
  CRASHLYTICS_ROW,
  DISPOSAL,
  OFFICER,
  OVERSEAS_HEADERS,
  OVERSEAS_REFUSAL,
  PROCESSOR_HEADERS,
  R2_ITEMS_SUPPORT,
  R2_RETENTION_SUPPORT,
  REMEDIES,
  RETENTION_HEADERS,
  REVENUECAT_OVERSEAS_ROW,
  REVENUECAT_ROW,
  SECURITY,
  SPONSOR_ITEM_AUTO,
  SPONSOR_NOTE,
  SPONSOR_PURPOSE,
  SPONSOR_RETENTION_ROW,
  SUPPORT_ITEM_AUTO,
  SUPPORT_ITEM_INPUT,
  SUPPORT_NOTE,
  SUPPORT_PURPOSE,
  SUPPORT_RETENTION_ROW,
  changes,
  cloudflareOverseasRow,
  cloudflareRow,
  noAutomaticCollection,
  rights,
  table,
} from './common'

/**
 * 첫장면 개인정보 처리방침.
 *
 * 이 앱은 서버가 없다 — 사진·음악·문구·프로젝트가 기기를 떠나지 않는다.
 * 외부로 나가는 것은 Firebase Crashlytics 진단 정보 하나뿐이고, 그래서 Play 데이터
 * 보안 설문에서 "제3자와 공유 = 아니오"로 신고할 수 있다.
 * **렌더링을 서버로 옮기는 날에는 이 문서와 설문을 함께 되돌려야 한다.**
 *
 * 앱 이름은 한국어 그대로 둔다 — 스토어에 `첫장면` 으로 올라가 있어 로마자로만 적으면
 * 이 방침이 어느 앱의 것인지 대조되지 않는다.
 */

const EFFECTIVE_DATE = 'August 20, 2026'
const LAST_REVISED = 'August 26, 2026'
const CRASH_RETENTION = '90 days'

const SECTIONS: readonly PolicySection[] = [
  {
    heading: 'Article 1. Purposes of processing personal data',
    paragraphs: [
      'The Operator processes personal data for the following purposes only. Should a purpose change, the Operator takes the steps required by Article 18 of the Personal Information Protection Act, including obtaining separate consent.',
    ],
    bullets: [
      'Keeping the service reliable — finding and fixing the places where the app crashes or errors',
      SUPPORT_PURPOSE,
      SPONSOR_PURPOSE,
    ],
  },
  {
    heading: 'Article 2. How the service is built — photos and video are handled on your device only',
    paragraphs: [
      '첫장면 (Cheotjangmyeon) works without an account. The photos and music you choose, the names, dates, places, and lines you type, and the video file that comes out are all processed and kept on your device alone; none of it is transmitted to the Operator. The Operator cannot access your photos or your video.',
      'The one thing transmitted to the Operator’s server is a message you send yourself from the Support screen in the top bar. Even then, no photo, music, video, or typed line is included: only the text you wrote, together with your app version, device model, operating system version, and language setting (Articles 3 and 5).',
      'Video encoding runs on the hardware encoder of your device (Android MediaCodec, iOS VideoToolbox). The finished file is written to the app cache and does not leave the device until you choose to save it to your gallery or share it. Once you share it, the policy of the app or service you picked governs from that point on.',
    ],
  },
  {
    heading: 'Article 3. Personal data processed',
    paragraphs: ['The items below are everything the Operator collects.'],
    bullets: [
      '[Collected automatically] App diagnostics — Firebase Crashlytics installation identifier and Firebase installation ID, screen and action names, app version and build, device model, operating system and device state, error messages, stack traces, and crash traces',
      SUPPORT_ITEM_INPUT,
      SUPPORT_ITEM_AUTO,
      SPONSOR_ITEM_AUTO,
    ],
    note: 'No account, name, phone number, location data, or contacts are collected. An email address is collected only where you type one into the support page yourself so that a reply can be sent, and a photograph only where you attach one to a support message yourself. Neither sensitive data under Article 23 nor unique identifying information under Article 24 of the Personal Information Protection Act is collected. Diagnostics never include your photos, video, music, or the lines you typed, and a support message carries nothing beyond what you write and attach.',
  },
  {
    heading: 'Article 4. Use of device permissions',
    bullets: [
      'Photo permission — used to put the photos you picked yourself into the video. The app remembers only the path of a chosen photo; it does not copy the photo or send it to a server',
      'Choosing a photo for a support message — the picker is the one built into the operating system, so the app sees only the photographs you hand it. Those, and only those, are sent to the Operator with your message',
      'File access — used only when you pick a music file yourself to use as background music',
      'Storage permission — used only to save the finished video to your gallery',
    ],
    note: 'The capture location stored in a photo (EXIF) is not read. The app supplies no music of its own; only files already on your device can be used as background music. If you add a copyrighted track and then screen or post the result publicly, obtaining the necessary licence is your responsibility.',
  },
  {
    heading: 'Article 5. Processing and retention periods',
    table: table(RETENTION_HEADERS, [
      [
        'Projects, photo selections, and finished videos',
        'On your device only (not held by the Operator)',
        'Not applicable',
      ],
      ['App diagnostics', `${CRASH_RETENTION} from collection`, 'Your consent (processor retention cycle)'],
      [...SUPPORT_RETENTION_ROW],
      [...SPONSOR_RETENTION_ROW],
    ]),
    note: `What is on your device disappears when you delete it from the list or delete the app. ${SUPPORT_NOTE} ${SPONSOR_NOTE} The Operator carries out no transaction that would create a retention duty under the Act on Consumer Protection in Electronic Commerce or similar legislation, so no personal data is kept separately under any statute.`,
  },
  {
    heading: 'Article 6. Provision of personal data to third parties',
    paragraphs: [
      'The Operator processes personal data only within the purposes set out in Article 1 and provides it to third parties only with your consent or where Article 17 or Article 18 of the Personal Information Protection Act applies. Photos, music, and video are transmitted to no external service whatsoever. Personal data is never sold.',
    ],
  },
  {
    heading: 'Article 7. Entrustment of personal data processing',
    paragraphs: [
      'To keep the service reliable, the Operator entrusts personal data processing as follows.',
    ],
    table: table(PROCESSOR_HEADERS, [
      [...CRASHLYTICS_ROW],
      cloudflareRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_ROW],
    ]),
    note: 'In each processing agreement, as required by Article 26 of the Personal Information Protection Act, the Operator sets out in writing the prohibition on processing beyond the stated purpose, technical and administrative safeguards, restrictions on sub-processing, supervision, and liability for damages, and supervises whether the processor handles personal data safely. If the way processing works changes, this policy is updated and notice given in advance.',
  },
  {
    heading: 'Article 8. Transfer of personal data abroad',
    paragraphs: [
      'The processor named in Article 7 is located outside Korea, and the entrustment transfers personal data abroad as follows.',
    ],
    table: table(OVERSEAS_HEADERS, [
      [...CRASHLYTICS_OVERSEAS_ROW],
      cloudflareOverseasRow(R2_ITEMS_SUPPORT, R2_RETENTION_SUPPORT),
      [...REVENUECAT_OVERSEAS_ROW],
    ]),
    note: OVERSEAS_REFUSAL,
  },
  DISPOSAL,
  rights(
    [
      'In the app — deleting a project from the list, or deleting the app, removes what was on your device along with it',
      'Deleting a support message — send us the reference number shown on the support page at the email address below and that message is deleted',
    ],
    'the text of support messages and app diagnostics (the Operator holds nothing else)',
  ),
  noAutomaticCollection(
    'The service shows no advertising. The only purchase the service offers is the optional “buy the developer a coffee” sponsorship, which unlocks nothing; payment is carried out by the Apple App Store or Google Play, and the Operator processes only the purchase record described in Articles 3, 5, 7, and 8.',
  ),
  CHILDREN,
  SECURITY,
  OFFICER,
  REMEDIES,
  changes(EFFECTIVE_DATE, [
    'August 26, 2026 — the photographs attached to support messages moved from the Operator’s own server disk to Cloudflare R2 object storage. Articles 6 and 7 were amended accordingly: image files are now held by a processor outside Korea. What is collected, why, and how long it is kept did not change',
    'August 26, 2026 — the support screen moved out of the app to a page at cottonray.com/support which the app opens inside itself. What is collected, why, and how long it is kept did not change',
    'August 24, 2026 — updated the purposes, items collected, and retention periods following the addition of photo attachments (up to three, chosen by you) and an optional email address to the in-app support screen, which the Operator uses to send a single reply from no-reply@cottonray.com',
  'August 24, 2026 — updated the purposes, items collected, retention periods, entrustment, and notice of transfers abroad following the introduction of the optional “buy the developer a coffee” sponsorship (in-app purchase; receipts verified by RevenueCat)',
    'August 15, 2026 — established (structured on Article 30 of the Personal Information Protection Act, including notice of transfers abroad)',
    'August 20, 2026 — updated the purposes, items collected, retention periods, and how to exercise your rights following the introduction of in-app support messages (feedback only, no contact details taken)',
    'August 19, 2026 — the site language changed to English and this policy was reissued in English. Nothing about how data is handled changed',
  ]),
]

export const CHEOTJANGMYEON_POLICY: PolicyDocument = {
  slug: 'cheotjangmyeon',
  title: '첫장면 (Cheotjangmyeon) Privacy Policy',
  intro: `${OPERATOR}, establishes and publishes the following privacy policy for 첫장면 (Cheotjangmyeon, “the service”) under Article 30 of the Personal Information Protection Act of the Republic of Korea, in order to protect the personal data of its users and to deal promptly and smoothly with any grievance arising from it.`,
  effectiveDate: EFFECTIVE_DATE,
  lastRevisedDate: LAST_REVISED,
  sections: SECTIONS,
}
